import { SavedPlaceList } from "./saved-places";

export type OnboardingWhyHere = "for_myself" | "for_someone";
export type OnboardingIntent = "find" | "add" | "manage_business";

/** user_details.onboarding_data — null until the user completes onboarding. */
export interface OnboardingData {
  why_here: OnboardingWhyHere | null;
  intents: OnboardingIntent[];
  no_specific_access_needs: boolean;
  matters_to_you_groups: string[];
}

/**
 * `sync-user`'s `data.scout_badge` block — the four tasks that earn the
 * Sociability Scout badge. Server-owned and authoritative; the client records
 * nothing, because the backend hangs step recording off the success path of
 * endpoints the app already calls.
 *
 * Ids and state only. All copy lives in the front end, keyed off these ids —
 * see `apps/native/src/features/scout-badge/constants.ts`.
 */
export type ScoutTaskId =
  | "accessibility-profile"
  | "add-experience"
  | "subscribe-venue"
  | "save-venue";

export type ScoutStepId =
  | "access-requirements"
  | "saved-filters"
  | "privacy"
  | "add-experience"
  | "subscribe-venue"
  | "save-venue";

export interface ScoutStep {
  id: ScoutStepId;
  isCompleted: boolean;
  /** `Y-m-d H:i:s`, or null. */
  completedAt: string | null;
}

export interface ScoutTask {
  id: ScoutTaskId;
  isCompleted: boolean;
  completedAt: string | null;
  /**
   * Populated only for `accessibility-profile`, which needs all three of its
   * steps. The server sends `[]` for the other three tasks, so never assume
   * there are steps to iterate.
   */
  steps: ScoutStep[];
}

export interface ScoutBadge {
  /** Feature flag plus start date. Everything reads as pending when false. */
  is_active: boolean;
  start_date: string | null;
  is_complete: boolean;
  completed_at: string | null;
  /**
   * Whole tasks only — `round(completedTasks / 4 * 100)`. Partial progress
   * through `accessibility-profile`'s three steps contributes nothing, so a
   * finer-grained bar has to be derived client-side from `tasks[].steps`.
   */
  completion_percentage: number;
  tasks: ScoutTask[];
  /**
   * Live totals since the start date. Deliberately allowed to disagree with
   * the task flags, which are sticky — a user who deletes their only review
   * keeps the completed task and drops to `experiences: 0`.
   *
   * Two caveats for anything outside the badge that reads these — the Manage
   * account contributions row does (`ProfileSummaryHeader`):
   * - `ScoutBadgeService::statusFor()` returns every count as 0 unless
   *   `SCOUT_BADGE_ENABLED` is on AND `now >= SCOUT_BADGE_START_DATE`.
   * - They are windowed on `created_at >= start_date`, so they are
   *   "contributions since the campaign began", not lifetime totals.
   */
  counts: {
    experiences: number;
    venues_subscribed: number;
    venues_saved: number;
    /**
     * Live Alerts the user raised — `business_notes` on the Live Alert action,
     * open or inactive. Narrower than the venue page's "Access alerts", which
     * counts Notes as well.
     */
    live_alerts: number;
    /** `review_photos` the user uploaded, general-user upload type only. */
    accessibility_photos: number;
  };
}

/**
 * `sync-user`'s `data.mfa` block (and the same block on a portal sign-in).
 * `required` is per portal and false for untagged clients, so gate on it
 * before reading anything else: a disabled portal reports `satisfied:false`
 * for every session. `satisfied` means this session's token carries the
 * server-side MFA stamp, which a portal-tagged Google/Microsoft login also gets.
 */
export interface MfaSummary {
  required: boolean;
  enrolled: boolean;
  satisfied: boolean;
  enforced: boolean;
  /** Days of business-portal grace left; null until the clock starts. */
  days_remaining: number | null;
  backup_codes_remaining: number | null;
  /** Only on the verify response: this sign-in spent a backup code. */
  used_backup_code?: boolean;
}

export interface UserRole {
  isAdmin: boolean;
  isSociabilityTeam: boolean;
  isSociabilityMapper: boolean;
  isSociabilityDashboardStats: boolean;
  isBusinessUser: boolean;
  isBrandUser: boolean;
  isDerbyUser: boolean;
  isAiAccessUser: boolean;
  maxPhotoUploadAllowedByRole: string;
  aiAccess: {
    alt_text: {
      enabled: boolean;
      venues: ["basic", "complex"];
      flow: "ai_first" | "manual_first" | "parallel";
    };
    tags: {
      enabled: boolean;
      venues: ["basic", "complex"];
      flow: "ai_first" | "manual_first" | "parallel";
    };
  };
}
export default interface User {
  id: number;
  name: string;
  email: string;
  email_verification: number;
  status: number;
  ability: string;
  profilePhoto: string;
  profile_photo: string;
  profile_color_code: string;
  gender: string;
  yearOfBirth: number;
  accessibilityNeed: string;
  level: string;
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresAt: string;
  socialLoginStatus: string;
  roles: UserRole;
  first_name: string;
  last_name: string;
  badgeImages?: string[];
  account_preferences: any[];
  accessibility_need: string;
  linked_social_account: string;
  saved_places: SavedPlaceList[];
  t_and_c_accept: number;
  total_points: number;
  job_title: string;
  job_function: string;
  job_description: string;
  businessOnboardingTasks: BusinessOnboardingTask[];
  onboarding_data: OnboardingData | null;
  /** Absent for older backends and on unauthenticated payloads. */
  scout_badge: ScoutBadge | null;
  /** Absent for older backends; present on every sync-user response since DEV-561. */
  mfa?: MfaSummary;
}
export interface BusinessOnboardingTask {
  id: string;
  title: string;
  description: string;
  isCompleted: boolean;
  buttonLabel: string;
}

export function userFromResponse(response: any): User | null {
  if (!response) return null;
  return {
    id: response.id,
    name: response.name,
    first_name: response.first_name,
    last_name: response.last_name,
    email: response.email,
    email_verification: response.email_verification,
    status: response.status,
    ability: response.ability,
    profilePhoto: response.profile_photo,
    profile_photo: response.profile_photo,
    profile_color_code: response.profile_color_code,
    gender: response.gender,
    yearOfBirth: response.year_of_birth,
    accessibilityNeed: response.accessibility_need,
    level: response.level,
    accessToken: response.access_token,
    refreshToken: response.refresh_token,
    tokenType: response.token_type,
    expiresAt: response.expires_at,
    socialLoginStatus: response.social_login_status,
    roles: {
      isAdmin: response.roles?.isAdmin ?? false,
      isSociabilityTeam: response.roles?.isSociabilityTeam ?? false,
      isSociabilityMapper: response.roles?.isSociabilityMapper ?? false,
      isSociabilityDashboardStats:
        response.roles?.isSociabilityDashboardStats ?? false,
      isBusinessUser: response.roles?.isBusinessUser ?? false,
      isBrandUser: response.roles?.isBrandUser ?? false,
      isDerbyUser: response.roles?.isDerbyUser ?? false,
      isAiAccessUser: response.roles?.isAiAccessUser ?? false,
      maxPhotoUploadAllowedByRole:
        response.roles?.maxPhotoUploadAllowedByRole ?? "0",
      aiAccess: response.roles?.aiAccess ?? {
        alt_text: {
          enabled: false,
          venues: "basic",
          flow: "manual_first",
        },
        tags: {
          enabled: false,
          venues: "basic",
          flow: "manual_first",
        },
      },
    },
    account_preferences: response.account_preferences,
    badgeImages: response.badge_images,
    accessibility_need: response.accessibility_need,
    linked_social_account: response.linked_social_account,
    saved_places: response.saved_places,
    t_and_c_accept: response.t_and_c_accept,
    total_points: response.total_points,
    job_title: response.job_title,
    job_function: response.job_function,
    job_description: response.job_description,
    businessOnboardingTasks: response.businessOnboardingTasks,
    onboarding_data: onboardingDataFromResponse(response.onboarding_data),
    scout_badge: scoutBadgeFromResponse(response.scout_badge),
    mfa: response.mfa ?? undefined,
  };
}

/** Tolerates the pre-onboarding API shape, where keys may be absent. */
export function onboardingDataFromResponse(raw: any): OnboardingData | null {
  if (!raw) return null;
  return {
    why_here: raw.why_here ?? null,
    intents: raw.intents ?? [],
    no_specific_access_needs: raw.no_specific_access_needs ?? false,
    matters_to_you_groups: raw.matters_to_you_groups ?? [],
  };
}

/**
 * Tolerates a backend that predates the badge, and an unauthenticated payload
 * — `scout_badge` is attached in `syncUserData()` rather than the shared
 * helper feeding the public-profile endpoint, so a guest never sees it.
 */
export function scoutBadgeFromResponse(raw: any): ScoutBadge | null {
  if (!raw) return null;
  return {
    is_active: !!raw.is_active,
    start_date: raw.start_date ?? null,
    is_complete: !!raw.is_complete,
    completed_at: raw.completed_at ?? null,
    completion_percentage: Number(raw.completion_percentage) || 0,
    tasks: Array.isArray(raw.tasks)
      ? raw.tasks.map((task: any) => ({
          id: task.id,
          isCompleted: !!task.isCompleted,
          completedAt: task.completedAt ?? null,
          steps: Array.isArray(task.steps)
            ? task.steps.map((step: any) => ({
                id: step.id,
                isCompleted: !!step.isCompleted,
                completedAt: step.completedAt ?? null,
              }))
            : [],
        }))
      : [],
    counts: {
      experiences: Number(raw.counts?.experiences) || 0,
      venues_subscribed: Number(raw.counts?.venues_subscribed) || 0,
      venues_saved: Number(raw.counts?.venues_saved) || 0,
      live_alerts: Number(raw.counts?.live_alerts) || 0,
      accessibility_photos: Number(raw.counts?.accessibility_photos) || 0,
    },
  };
}

export function isUserProfilePublic(user?: User): boolean {
  const preference = user?.account_preferences.find(
    (e) => e.key === "public_activities",
  );
  if (!preference) {
    return true;
  }
  return preference.value === "1";
}

export function isUserProfilePublicFromPrefs(preferences: any[]): boolean {
  const preference = preferences.find((e) => e.key === "public_activities");
  if (!preference) {
    return true;
  }
  return preference.value === "1";
}

export function isUserDisabilityPublic(user?: User): boolean {
  const preference = user?.account_preferences.find(
    (e) => e.key === "disability_visible",
  );
  if (!preference) {
    return true;
  }
  return preference.value === "1";
}
